// Анимация выстрела: катапульта бросает валуны по высокой дуге, эльфы дают залп
// стрел. Снарядов в залпе столько, каков размер отряда. Числа подобраны под
// звук выстрела (SoundManager.playShooting): у катапульты удар звучит через
// ~1 с после пуска, у эльфов крик цели — через 300 мс, поэтому последний валун
// падает около секунды, а первая стрела долетает около трети.
//
// Итог выстрела (урон на цели, поврежденное строение, линия выстрела) на поле
// появляется в момент ПОПАДАНИЯ, а не пуска (см. HexGridRenderer.showShotAnimation).
// Чужой выстрел при этом укладывается в шаг темпа действий (см. pace ниже)

export const SHOT_KINDS = {
  // Катапульта: валуны по высокой дуге, тень на земле, осколки и пыль при ударе
  BOULDER: 'boulder',
  // Эльфы: залп стрел по пологой дуге
  ARROWS: 'arrows',
};

// Текстуры, которые рисуются кодом (см. utils/effects/shotProjectileTextures):
// стрела и искра — картинок для них в public нет; валун — запасной, на случай,
// если картинки валунов не загрузились
export const SHOT_TEXTURE_KEYS = {
  BOULDER: 'shot_boulder',
  ARROW: 'shot_arrow',
  SPARK: 'shot_spark',
};

// Картинки валунов: у каждого камня залпа своя, случайная. Ключи и пути должны
// совпадать с загрузкой в GameScene.preload(). Сами файлы собирает
// developer_artifacts/scripts/generateShotEffectSprites.cjs
export const SHOT_PROJECTILE_IMAGES = [
  {key: 'shot_projectile_1', path: '/img/effects/shooting/projectile_1.png'},
  {key: 'shot_projectile_2', path: '/img/effects/shooting/projectile_2.png'},
  {key: 'shot_projectile_3', path: '/img/effects/shooting/projectile_3.png'},
];
// Сторона картинки валуна: размеры на поле считаются от размера ячейки
export const SHOT_PROJECTILE_IMAGE_SIZE = 128;

// Спрайт-лист осколков, разлетающихся при падении валуна. Кадры идут от самых
// крупных осколков к самым мелким: первые bigFrameCount — «глыбы», остальные —
// «крошка»
export const SHOT_SHARDS_SHEET = {
  key: 'shot_shards',
  path: '/img/effects/shooting/shards.png',
  frameWidth: 64,
  frameHeight: 64,
  frameCount: 15,
  bigFrameCount: 7,
};

// Снарядов в залпе — по одному на единицу размера отряда. Потолок — страховка
// от непредвиденно большого отряда (у стрелков больше 20 не бывает): сотня
// спрайтов разом поле не украсит
export const SHOT_VOLLEY_MAX_PROJECTILES = 20;

export const SHOT_ANIMATION = {
  boulder: {
    // Пауза перед вылетом первого валуна: катапульта «выдыхает» пыль
    windupMs: 80,
    // Валуны уходят один за другим; большой залп укладывается в окно — шаг
    // между камнями сжимается
    staggerMs: 90,
    staggerWindowMs: 270,
    // Разброс момента вылета — доля шага между камнями: залп не идет метрономом
    launchJitterShare: 0.3,
    // Время полета одиночного валуна. В залпе камни летят быстрее ровно на
    // свое окно вылета: ПОСЛЕДНИЙ валун падает тогда же, когда упал бы
    // одиночный, — удар должен попасть в звук (~1 с от пуска)
    flightMs: {base: 780, perHex: 30, max: 930},
    // Разброс времени полета камней залпа, доля длительности
    durationJitter: 0.06,
    // Высота дуги в размерах ячейки (hex.size): растет с дальностью
    arcHeightHexes: {base: 1.3, perHex: 0.28, max: 2.9},
    // У каждого камня залпа дуга чуть своя
    arcJitter: {min: 0.8, max: 1.2},
    // Диаметр валуна на земле в долях размера ячейки и во сколько раз он
    // крупнее в верхней точке дуги (ближе к зрителю)
    sizeFactor: 0.42,
    // Камни залпа разного размера
    sizeJitter: {min: 0.85, max: 1.12},
    peakScaleMultiplier: 1.6,
    // Сколько оборотов делает валун за полет
    spinTurns: {min: 0.5, max: 1.3},
    // Разброс точек вылета вокруг катапульты и точек падения вокруг цели, в
    // долях размера ячейки. Одиночный валун летит точно из центра в центр
    startSpreadFactor: 0.22,
    endSpreadFactor: 0.36,
    // Тень на земле: ширина текстуры тени в долях размера ячейки (темное ядро
    // у нее — около трети ширины, остальное мягкий ореол); в верхней точке
    // дуги тень мельче и бледнее — валун высоко
    shadow: {sizeFactor: 1.25, alpha: 0.6, peakScaleMultiplier: 0.6, peakAlpha: 0.3},
    // Пыль: у катапульты при пуске и на месте последнего удара (доля
    // «тяжести» жетона, см. showBuildingPlacementDust)
    launchDustIntensity: 0.3,
    impactDustIntensity: 0.75,
    // Камера вздрагивает от падения каждого валуна, если выстрел не промах (см.
    // checkIfShotMissed): промах не должен ощущаться ударом. Камни большого залпа падают
    // чаще, чем длится дрожь, и сливаются в один гул (см. shakeFieldCamera)
    impactShake: {durationMs: 130, intensity: 0.002},
  },
  arrows: {
    // Стрелы уходят одна за другой; большой залп укладывается в окно
    staggerMs: 42,
    staggerWindowMs: 252,
    launchJitterShare: 0.3,
    flightMs: {base: 245, perHex: 14, max: 320},
    // Пологая дуга
    arcHeightHexes: {base: 0.22, perHex: 0.1, max: 0.75},
    // У каждой стрелы дуга чуть своя: залп не идет одной линией
    arcJitter: {min: 0.8, max: 1.2},
    // Длина стрелы в долях размера ячейки
    lengthFactor: 1.05,
    // Разброс точек вылета вокруг стрелка и точек попадания вокруг цели, в
    // долях размера ячейки: стреляет отряд, а не один лучник. Одиночная стрела
    // летит точно из центра в центр
    startSpreadFactor: 0.3,
    endSpreadFactor: 0.2,
    // Искра в точке попадания каждой стрелы
    spark: {sizeFactor: 0.55, durationMs: 190, startScaleMultiplier: 0.4},
  },
  // Падение валуна: камень раскалывается, осколки разлетаются от точки удара
  // В СТОРОНЫ, вдоль земли; из-под удара выбивает клуб пыли. Вся случайность
  // живет в utils/effects/buildShotDebris
  debris: {
    // СКОЛЬКО ОСКОЛКОВ. perBoulder.max — у одиночного валуна и небольшого
    // залпа; totalBudget — потолок на весь залп: при большем числе валунов
    // на каждый приходится totalBudget / число валунов (иначе цель утонет в
    // камнях). Хочется гуще — поднимать оба числа
    totalBudget: 40,
    perBoulder: {min: 1, max: 11},
    // Доля крупных осколков («глыб»); хотя бы одна глыба есть всегда
    bigShare: 0.3,
    // Размер осколка в долях размера ячейки
    bigSizeFactor: {min: 0.26, max: 0.4},
    smallSizeFactor: {min: 0.13, max: 0.22},
    // Как далеко по земле улетает осколок, в долях размера ячейки. Глыбы
    // тяжелее: летят ближе
    distanceFactor: {min: 0.7, max: 1.8},
    bigReachMultiplier: 0.65,
    // Подскок осколка — доля его пути по земле. Он намеренно мал: осколки
    // разлетаются в стороны, чуть по дуге. С высокой дугой разлет выглядел
    // фонтаном — камни летели вверх, а не от точки удара
    arcShare: {min: 0.06, max: 0.2},
    // Осколки летят во все стороны, но больше — дальше по ходу выстрела:
    // камень несет вперед. Доля размера ячейки
    forwardDriftFactor: 0.3,
    // Разброс угла — доля своего сектора: без него осколки шли бы ровной звездой
    angleJitterShare: 0.4,
    // Вид сверху и чуть сбоку: по вертикали экрана разлет сплющен
    groundSquash: 0.8,
    // Разлет быстрый: удар, а не листопад. Осколок вылетает резко и тормозит
    // (см. HexGridRenderer.showShotDebris)
    durationMs: {min: 380, max: 640},
    delayMs: {min: 0, max: 45},
    spinTurns: {min: 0.5, max: 1.8},
    // С этой доли пути осколок тает; к концу он еще и мельчает (уходит вдаль)
    fadeFromProgress: 0.6,
    endScaleMultiplier: 0.7,
    // Вспышка в точке удара каждого валуна (та же искра, что у стрел, только
    // крупнее): короткий светлый хлопок, от которого и летят осколки
    flash: {sizeFactor: 0.95, durationMs: 170, startScaleMultiplier: 0.35},
    // Клубы пыли из-под удара каждого валуна (текстуры пыли построек)
    puffs: {
      count: {few: 2, many: 1},
      // С этого числа валунов в залпе клубов на каждый меньше
      manyBouldersFrom: 5,
      startSizeFactor: 0.3,
      endSizeFactor: {min: 0.85, max: 1.2},
      offsetFactor: 0.12,
      alpha: 0.7,
      durationMs: {min: 380, max: 520},
    },
  },
  impact: {
    // Значка урона над целью нет — ни над отрядом, ни над строением: он
    // закрывал бы цель, а попадание видно и так (осколки и пыль валуна, искры
    // стрел, «−N» у отряда, поврежденное строение, линия выстрела).
    // Отряд-цель вздрагивает: смещение от стрелка и обратно. Не мигание —
    // один импульс
    shakeDistanceFactor: {boulder: 0.12, arrows: 0.07},
    shakeMs: 70,
  },
  // Стрелок «отдает»: короткий толчок от цели и обратно
  recoil: {distanceFactor: {boulder: 0.09, arrows: 0.05}, ms: 70},
  // Чужой выстрел в живой партии (бот, соперник по сети) идет в темпе, который
  // задает не зритель: следующее действие придет через шаг темпа, и им может
  // оказаться завершение этапа — после него итог выстрела с поля уходит. Залп
  // сжимается по времени так, чтобы попасть в цель раньше и оставить итогу
  // время побыть на поле (см. resolveShotTimeFactor). Звук идет за залпом: его
  // задержки сжимаются тем же множителем (SoundManager.playShooting)
  pace: {
    // Доля шага темпа, которая остается итогу выстрела после попадания
    resultHoldShare: 0.3,
    // Сильнее этого залп не сжимается (доля обычной длительности): быстрее он
    // уже не читается — анимации тогда нет, итог виден сразу
    minTimeFactor: 0.45,
  },
  // Панель с результатом стрельбы в футере открывается не в миг попадания, а
  // через эту паузу: итог на поле (урон на цели, линия выстрела) сначала
  // виден целиком, и только потом низ поля закрывает панель (см.
  // utils/shooting/useShootingResultRevealed). Пауза — только у своего
  // выстрела: чужой идет в чужом темпе (см. pace), до следующего действия
  // остается доля шага, и с паузой панель мелькнула бы на несколько кадров
  // или не успела бы открыться вовсе — у него она открывается с попаданием
  resultPanelDelayMs: 500,
  // Запас сверх расчетной длительности, после которого залп считается
  // зависшим (скрытая вкладка: твины стоят, часы идут) и итог показывается
  // без анимации
  staleMarginMs: 1500,
};

// Линия уже сделанного выстрела: спокойный пунктир вместо мигающей полосы
export const SHOT_TRAJECTORY = {
  lineWidth: 7,
  alpha: 0.6,
  dashLength: 16,
  gapLength: 11,
};
